import globalUseStyles from '../../../../styles/globalCustomStyles';

import { Card, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useGetList, useRecordContext, useTranslate } from 'react-admin';
import InfoTooltipIcon from "../../../../assets/InfoTooltipIcon";
import ChartLoading from '../../../aegeon/charts/ChartLoading';
import CustomTooltip from '../../../aegeon/CustomTooltip';
import { value_with_unit } from '../../../aegeon/helper';
import ChooseUsageButton from '../../../Buttons/ChooseUsageButton';
import ChooseYearButton from '../../../Buttons/ChooseYearButton';
import CustomTable from '../../../Table/CustomTable';
import DeviceQualitiesReceiver from '../../CustomComponents/DeviceQualitiesReceiver';

const SiteEnergeticPerformanceGeneral = (props) => {
  const globalClasses = globalUseStyles();
  const t = useTranslate();
  const { type, isGraph, startDate, endDate, setYear, dpeUsage, crremUsage, setDpeUsage, setCrremUsage, crremUsages, dpeUsages } = props;
  const record = useRecordContext(props);
  const siteId = record?.id;
  const [missingRows, setMissingRows] = useState({ energetic: [] });
  const [percentage, setPercentage] = useState({});
  const [concatenedMissingRows, setConcatenedMissingRows] = useState([]);
  const [quality, setQuality] = useState(null);

  const { data: devices } = useGetList(
    'devices',
    {
      filter: {
        siteIds: { contains: siteId },
      },
    }
  );

  const getQuality = (percentage, synoptic_quality) => {
    const limit_B = 10;
    const limit_C = 20;
    if (percentage < 5 && synoptic_quality === "A") {
      return "A";
    }
    else if (percentage < limit_B && (synoptic_quality === "B" || synoptic_quality === "A")) {
      return "B";
    }
    else if (percentage < limit_C && (synoptic_quality === "B" || synoptic_quality === "A")) {
      return "C";
    }
    return "D";
  }

  useEffect(() => {
    let concatenedMissingRows = [
      { name: "Compteur", values: ["Mois manquants"] }
    ]
    let missingRowsGroupedByDevice = {}
    missingRows["energetic"].forEach((row, index) => {
      if (index > 0) {
        if (row.values[0] !== "") {
          missingRowsGroupedByDevice[row.name] = row.values[0]
        }
      }
    })
    Object.keys(missingRowsGroupedByDevice).forEach((device) => {
      concatenedMissingRows.push({ name: device, values: [missingRowsGroupedByDevice[device]] })
    })
    setConcatenedMissingRows(concatenedMissingRows)
  }, [missingRows])

  useEffect(() => {
    if (percentage["energetic"] !== undefined && record && !quality) {
      let quality = getQuality(percentage["energetic"], record?.airtable?.current_synoptic_quality_reporting);
      setQuality(quality);
    }
  }, [percentage, record]);

  const title = t('sites.energetic_performance.general.title');
  const subtitle = t('sites.energetic_performance.general.subtitle');
  const tooltip_text = t('sites.energetic_performance.general.tooltip_text');

  let period = startDate && endDate ? `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}` : t('sites.energetic_performance.general.period_no_data');

  let dpe_usage = record?.airtable?.dpe_usage ? t("sites.energetic_performance.general.usages." + record?.airtable?.dpe_usage) : t("sites.energetic_performance.general.usages.BUREAUX");
  let crrem_usage = record?.airtable?.crrem_usage ? t("sites.energetic_performance.general.usages." + record?.airtable?.crrem_usage) : t("sites.energetic_performance.general.usages.OFFICE");

  const rows = [
    { name: t('sites.energetic_performance.general.validated_client_surface'), value: value_with_unit(record?.airtable?.validated_client_surface, "m²"), tooltip: 'sites.general_information.validated_client_surface_no_data', tooltip_info: 'sites.energetic_performance.general.validated_client_surface_tooltip_info' },
    { name: t('sites.energetic_performance.general.thermical_surface'), value: value_with_unit(record?.airtable?.validated_client_surface * 1.1, "m²Sth"), tooltip: 'sites.general_information.thermical_surface_no_data', tooltip_info: 'sites.energetic_performance.general.thermical_surface_tooltip_info' },
    { name: t('sites.energetic_performance.general.period'), value: period, tooltip: 'sites.general_information.validated_client_volume_no_data' },
    { name: t('sites.energetic_performance.general.synoptic_quality'), value: record?.airtable?.current_synoptic_quality_reporting, tooltip_info: 'sites.energetic_performance.general.synoptic_quality_tooltip_info' },
    { name: t('sites.energetic_performance.general.percentage'), value: `${percentage["energetic"]}%`, tooltip: 'sites.energetic_performance.general.percentage_no_data' },
    { name: t('sites.energetic_performance.general.quality'), value: quality, tooltip_info: 'sites.energetic_performance.general.quality_tooltip_info', tooltip_info_color: (!["A", "B"].includes(quality)) ? "#f44336" : "" },
    { name: t('sites.energetic_performance.general.dpe_usage'), value: dpe_usage, tooltip: 'sites.energetic_performance.general.dpe_usage_no_data', tooltip_with_value: !record?.airtable?.usage_dpe },
    { name: t('sites.energetic_performance.general.crrem_usage'), value: crrem_usage, tooltip: 'sites.energetic_performance.general.crrem_usage_no_data', tooltip_with_value: !record?.airtable?.usage_crrem },
  ]

  return (
    <Card className={globalClasses.DetailCard}>
      <div className={globalClasses.DetailCardTableTitle}>
        <Typography variant="h8">{title}</Typography>
        <CustomTooltip title={tooltip_text} icon={<InfoTooltipIcon />} />
      </div>
      <div className={globalClasses.DetailCardTableSubtitle}>
        {subtitle}
      </div>
      <div>
        <DeviceQualitiesReceiver
          devices={devices}
          reportingStartDate={startDate}
          reportingEndDate={endDate}
          setRows={setMissingRows}
          setPercentage={setPercentage}
          setScore={() => { }}
        />
        <div className={globalClasses.evolutionButtons}>
          <ChooseYearButton
            title={t(`sites.energetic_performance.general.choose_year`)}
            year={startDate ? moment(startDate).year() : moment().year() - 1}
            setYear={(year) => { setYear(year) }}
          />
          <ChooseUsageButton
            title={t(`sites.energetic_performance.general.choose_dpe_usage`)}
            usage={dpeUsage}
            setUsage={setDpeUsage}
            usages={dpeUsages}
          />
          <ChooseUsageButton
            title={t(`sites.energetic_performance.general.choose_crrem_usage`)}
            usage={crremUsage}
            setUsage={setCrremUsage}
            usages={crremUsages}
          />
        </div>
        <Grid2 container spacing={4}>
          <Grid2 small={6} medium={6} big={6}>
            {percentage["energetic"] === undefined ? <ChartLoading /> :
              <CustomTable rows={rows} />
            }
          </Grid2>
          <Grid2 small={6} medium={6} big={6}>
            <div className={globalClasses.performanceMissingBills}>
              {concatenedMissingRows?.length > 1 &&
                <CustomTable key="missing" list={true} rows={concatenedMissingRows} defaultTooltip={"sites.no_data_tooltip"} />
              }
            </div>
          </Grid2>
          <Grid2 small={12} medium={12} big={12}>
            {quality && quality !== "A" &&
              <div className={globalClasses.warningBanner + " " + (quality !== "B" ? globalClasses.warningBannerHigh : "")}>
                <Typography variant="h9">
                  {t('sites.energetic_performance.general.warning') + quality + " " + t('sites.energetic_performance.general.warning_2') + percentage["energetic"] + t('sites.energetic_performance.general.warning_3') + record?.airtable?.current_synoptic_quality_reporting + t('sites.energetic_performance.general.warning_4')}
                </Typography>
              </div>
            }
          </Grid2>
        </Grid2>
      </div>
    </Card>
  )
};

export default SiteEnergeticPerformanceGeneral; 